import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Globe, XCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { LanderSetupSteps } from "@/components/settings/LanderSetupSteps";

const INPUT = "mt-1 w-full border-2 border-black bg-white px-2 py-1.5 text-sm";
const BTN = "border-2 border-black px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-100 disabled:opacity-40";

function CheckList({ check }) {
  if (!check) return null;
  return (
    <ul className="mt-3 space-y-1 text-xs" data-testid="lander-check-results">
      {check.items.map((i) => (
        <li key={i.url} className="flex items-start gap-1.5" data-testid={`lander-check-${i.label.toLowerCase().replace(/\s+/g, "-")}`}>
          {i.ok ? <CheckCircle size={14} weight="fill" className="text-emerald-600 mt-0.5" /> : <XCircle size={14} weight="fill" className="text-red-600 mt-0.5" />}
          <span><b>{i.label}</b> <span className="font-mono text-[10px] text-zinc-500">{i.url}</span> — {i.detail}</span>
        </li>
      ))}
    </ul>
  );
}

// Platform settings: put every visitor page on a second domain (e.g. casequoter.com).
export function LanderDomainCard() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const take = (data) => { setD(data); setForm({ domain: data.domain, brand_name: data.brand_name, redirect_old: data.redirect_old, home_path: data.home_path }); };
  useEffect(() => { api.get("/admin/lander-domain").then((r) => take(r.data)).catch(() => setD(false)); }, []);
  if (d === false || !form) return null;
  const run = (key, call, ok) => {
    setBusy(key);
    call().then((r) => { take(r.data); if (ok) toast.success(ok(r.data)); })
      .catch((e) => toast.error(e.response?.data?.detail || "Something went wrong"))
      .finally(() => setBusy(""));
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = ["domain", "brand_name", "redirect_old", "home_path"].some((k) => form[k] !== d[k]);
  const check = d.last_check;
  return (
    <section className="mt-6 border-2 border-black bg-white p-5" data-testid="lander-domain-card">
      <div className="flex flex-wrap items-center gap-2">
        <Globe size={18} weight="bold" />
        <h2 className="font-chivo font-black uppercase tracking-tighter text-lg">Landing page domain</h2>
        <span className={`ml-auto px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${d.enabled ? "bg-emerald-600 text-white" : "bg-zinc-200"}`} data-testid="lander-status">
          {d.enabled ? `On · ${d.domain}` : "Off"}
        </span>
      </div>
      <p className="mt-1 text-xs text-zinc-600 max-w-3xl">
        Show every landing and thank-you page on a separate domain. This admin stays at {d.platform_origin.replace(/^https?:\/\//, "")}. Leads, buyers and reports don&apos;t change.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs"><span className="label-mono">Domain</span>
          <input className={INPUT} value={form.domain} onChange={(e) => set("domain", e.target.value)} placeholder="casequoter.com" data-testid="lander-domain-input" />
        </label>
        <label className="text-xs"><span className="label-mono">Brand shown on those pages</span>
          <input className={INPUT} value={form.brand_name} maxLength={60} onChange={(e) => set("brand_name", e.target.value)} placeholder="CaseQuoter" data-testid="lander-brand-input" />
          <span className="text-[10px] text-zinc-500">Replaces &ldquo;Conecme&rdquo; in the header, footer, consent and privacy pages. Your company name (Jellis Enterprise LLC) stays.</span>
        </label>
        <label className="text-xs"><span className="label-mono">Page people see at the bare domain</span>
          <input className={INPUT} value={form.home_path} onChange={(e) => set("home_path", e.target.value)} placeholder="/lp/standard" data-testid="lander-home-input" />
        </label>
        <label className="flex items-center gap-2 text-xs self-end pb-2">
          <input type="checkbox" checked={form.redirect_old} onChange={(e) => set("redirect_old", e.target.checked)} data-testid="lander-redirect-old" />
          Send old lander links on this admin address to the new domain
        </label>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={`${BTN} bg-black text-white hover:bg-zinc-800`} disabled={!dirty || busy} data-testid="lander-save"
                onClick={() => run("save", () => api.put("/admin/lander-domain", form), () => "Saved")}>{busy === "save" ? "Saving…" : "Save"}</button>
        <button type="button" className={`${BTN} bg-white hover:bg-zinc-100`} disabled={!d.domain || dirty || busy} data-testid="lander-check"
                onClick={() => run("check", () => api.post("/admin/lander-domain/check"), (r) => (r.check.ok ? "Everything is connected" : "Not connected yet"))}>{busy === "check" ? "Checking…" : "Check connection"}</button>
        <button type="button" className={`${BTN} ${d.enabled ? "bg-white hover:bg-red-50" : "bg-emerald-700 text-white hover:bg-emerald-800"}`} disabled={!d.domain || dirty || busy} data-testid="lander-enable"
                onClick={() => run("enable", () => api.post("/admin/lander-domain/enable", { enabled: !d.enabled }), (r) => (r.enabled ? `Visitor pages now use ${r.domain}` : "Turned off"))}>
          {busy === "enable" ? "Working…" : d.enabled ? "Turn off" : "Turn on"}
        </button>
      </div>
      {check && <div className="mt-2 text-[10px] text-zinc-500" data-testid="lander-last-check">Last checked {new Date(check.checked_at).toLocaleString()}</div>}
      <CheckList check={check} />
      {d.domain && d.worker_script && <LanderSetupSteps domain={d.domain} script={d.worker_script} />}
    </section>
  );
}
